/**
 * useSidebar Hook
 *
 * Custom hook that provides all sidebar functionality including:
 * - Navigation state and actions
 * - Active menu detection
 *
 * Performance optimizations:
 * - Memoized keyboard handlers
 *
 * @module useSidebar
 */

import { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNavigation } from '../context';
import { getPermittedNavigationAreas, getPermittedNavigationUtilities } from '../constants';
import { useAuth } from '../../../contexts/auth/AuthContext';

/**
 * useSidebar Hook
 *
 * @returns {Object} Sidebar state and handlers
 */
export const useSidebar = () => {
  const navigate = useNavigate();
  const { hasPermission, user } = useAuth();

  // Get all navigation state and actions
  const {
    isMobileView,
    isSidebarOpen,
    activeMenu,
    activeAncestor,
    isCollapsed,
    toggleSidebar,
    openSidebar,
    closeSidebar,
    toggleCollapse
  } = useNavigation();

  const navigationAreas = useMemo(
    () => getPermittedNavigationAreas(hasPermission, user),
    [hasPermission, user]
  );

  const navigationUtilities = useMemo(
    () => getPermittedNavigationUtilities(hasPermission, user),
    [hasPermission, user]
  );

  const activeNavigationArea = useMemo(
    () => navigationAreas.find(area => area.items.some(item => (
      item.id === activeMenu || item.id === activeAncestor
    ))) || null,
    [activeAncestor, activeMenu, navigationAreas]
  );

  /**
   * Handle menu item click
   * Navigates to path and closes sidebar on mobile
   *
   * @param {string} menuId - Menu item ID
   * @param {string} path - Route path
   */
  const handleMenuClick = useCallback((menuId, path) => {
    if (path) {
      navigate(path);
    }

    // Close sidebar on mobile after navigation
    if (isMobileView) {
      closeSidebar();
    }
  }, [navigate, isMobileView, closeSidebar]);

  return {
    // State
    isMobileView,
    isSidebarOpen,
    activeMenu,
    activeAncestor,
    isCollapsed,
    navigationAreas,
    navigationUtilities,
    activeNavigationArea,

    // Actions
    toggleSidebar,
    openSidebar,
    closeSidebar,
    toggleCollapse,

    // Handlers
    handleMenuClick
  };
};
